import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes, faTrashAlt, faPen, faUndo, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";

const JobRequestsTable = ({
  theme,
  requests,
  canEdit,
  onAccept,
  onDecline,
  onCancel,
  currentUserId
}) => {
  const [declineReasonId, setDeclineReasonId] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeEditingId, setActiveEditingId] = useState(null);
  const [isMinimised, setIsMinimised] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Calculate pending count and minimisability
  const pendingCount = requests ? requests.filter(r => r.status === 'pending').length : 0;
  const canMinimise = pendingCount === 0;

  // Auto-expand if a new pending request arrives
  useEffect(() => {
    if (!canMinimise) {
      setIsMinimised(false);
    }
  }, [canMinimise]);

  // Default to collapsed on initial render if pending count is 0
  useEffect(() => {
    if (requests && requests.length > 0 && !hasInitialized) {
      const initialPendingCount = requests.filter(r => r.status === 'pending').length;
      if (initialPendingCount === 0) {
        setIsMinimised(true);
      }
      setHasInitialized(true);
    }
  }, [requests, hasInitialized]);

  if (!requests || requests.length === 0) return null;

  // Custom theme colors for dark/light modes
  const isDark = theme === "dark";
  const containerBg = isDark ? "#1f1f1f" : "#fff";
  const containerBorder = isDark ? "1px solid #444" : "1px solid #ddd";
  const tableHeaderBg = isDark ? "rgba(255, 255, 255, 0.05)" : "#f8f9fa";
  const tableHeaderColor = isDark ? "#fff" : "#333";
  const tableRowBorder = isDark ? "1px solid #333" : "1px solid #eee";
  const textColor = isDark ? "#e0e0e0" : "#333";
  const subTextColor = isDark ? "#aaa" : "#666";

  // Status-specific pastel color schemes
  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case "pending":
        return {
          backgroundColor: isDark ? "rgba(255, 193, 7, 0.15)" : "#fff3cd",
          color: isDark ? "#ffc107" : "#664d03",
          border: isDark ? "1px solid rgba(255, 193, 7, 0.3)" : "1px solid #ffe69c"
        };
      case "accepted":
        return {
          backgroundColor: isDark ? "rgba(40, 167, 69, 0.15)" : "#d1e7dd",
          color: isDark ? "#28a745" : "#0a3622",
          border: isDark ? "1px solid rgba(40, 167, 69, 0.3)" : "1px solid #a3cfbb"
        };
      case "declined":
        return {
          backgroundColor: isDark ? "rgba(220, 53, 69, 0.15)" : "#f8d7da",
          color: isDark ? "#dc3545" : "#58151c",
          border: isDark ? "1px solid rgba(220, 53, 69, 0.3)" : "1px solid #f1aeb5"
        };
      case "cancelled":
        return {
          backgroundColor: isDark ? "rgba(108, 117, 125, 0.15)" : "#e2e3e5",
          color: isDark ? "#9e9e9e" : "#2b2f32",
          border: isDark ? "1px solid rgba(108, 117, 125, 0.3)" : "1px solid #c4c8cb"
        };
      default:
        return {};
    }
  };

  const handleDeclineSubmit = async (requestId) => {
    if (declineReason.trim() === "") return;
    setIsProcessing(true);
    await onDecline(requestId, declineReason.trim());
    setIsProcessing(false);
    setDeclineReasonId(null);
    setDeclineReason("");
  };

  return (
    <div 
      style={{
        background: containerBg,
        border: containerBorder,
        borderRadius: "8px",
        padding: "16px",
        marginBottom: isMinimised ? "12px" : "24px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        transition: "margin-bottom 0.2s ease"
      }}
    >
      {/* Title Header with dynamic minimize button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isMinimised ? "0px" : "16px" }}>
        <h3 style={{ margin: 0, fontSize: "1.25em", color: tableHeaderColor, fontWeight: "600" }}>
          🔔 Job Requests ({pendingCount} Pending)
        </h3>
        {canMinimise && (
          <button
            onClick={() => setIsMinimised(prev => !prev)}
            style={{
              background: "none",
              border: "none",
              color: isDark ? "#38bdf8" : "#0284c7",
              cursor: "pointer",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontWeight: "600",
              padding: "4px 8px",
              borderRadius: "4px",
              transition: "all 0.15s ease"
            }}
            onMouseOver={(e) => e.currentTarget.style.background = isDark ? "rgba(56,189,248,0.1)" : "rgba(2,132,199,0.05)"}
            onMouseOut={(e) => e.currentTarget.style.background = "none"}
          >
            {isMinimised ? (
              <>
                Show Requests <FontAwesomeIcon icon={faChevronDown} style={{ width: 12 }} />
              </>
            ) : (
              <>
                Minimise <FontAwesomeIcon icon={faChevronUp} style={{ width: 12 }} />
              </>
            )}
          </button>
        )}
      </div>

      {!isMinimised && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px", textAlign: "left" }}>
          <thead>
            <tr style={{ background: tableHeaderBg, borderBottom: containerBorder, backgroundColor: theme === "dark" ? "#fff" : "#a7a4a4" }}>
              <th style={{ padding: "12px", color: theme === "dark" ? "#000" : "#333", fontWeight: "600" }}>Requested By</th>
              <th style={{ padding: "12px", color: theme === "dark" ? "#000" : "#333", fontWeight: "600" }}>Customer</th>
              <th style={{ padding: "12px", color: theme === "dark" ? "#000" : "#333", fontWeight: "600" }}>Route</th>
              <th style={{ padding: "12px", color: theme === "dark" ? "#000" : "#333", fontWeight: "600" }}>Pallets & Weight</th>
              <th style={{ padding: "12px", color: theme === "dark" ? "#000" : "#333", fontWeight: "600" }}>Booking</th>
              <th style={{ padding: "12px", color: theme === "dark" ? "#000" : "#333", fontWeight: "600" }}>Comments</th>
              <th style={{ padding: "12px", color: theme === "dark" ? "#000" : "#333", fontWeight: "600" }}>Status</th>
              <th style={{ padding: "12px", color: theme === "dark" ? "#000" : "#333", fontWeight: "600", textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((req) => {
              // Resolve requested by name
              const requester = req.profiles;
              const requesterName = requester 
                ? `${requester.name || ''} ${requester.surname || ''}`.trim() || "A user"
                : "A user";

              const processor = req.processed_by_profile;
              const processorName = processor
                ? `${processor.name || ''} ${processor.surname || ''}`.trim() || "Admin"
                : "Admin";

              const badgeStyle = getStatusBadgeStyle(req.status);

              return (
                <tr 
                  key={req.id} 
                  style={{ 
                    borderBottom: tableRowBorder,
                    opacity: req.status === "cancelled" ? 0.65 : 1,
                    transition: "all 0.2s ease"
                  }}
                >
                  {/* Requested By */}
                  <td style={{ padding: "12px", color: textColor, fontWeight: "500" }}>
                    {requesterName}
                    <div style={{ fontSize: "11px", color: subTextColor, marginTop: "2px" }}>
                      Requested: {new Date(req.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </td>

                  {/* Customer details */}
                  <td style={{ padding: "12px", color: textColor }}>
                    {req.customer_company && req.customer_company !== req.company && (
                        <div style={{ fontSize: "11px", color: subTextColor, marginBottom: "2px", fontWeight: "500", opacity: 0.7 }}>{req.customer_company}</div>
                    )}
                    <div style={{ fontWeight: "600" }}>{req.company}</div>
                    <div style={{ fontSize: "11px", color: subTextColor, marginTop: "2px" }}>
                      {req.address}, {req.postcode}
                    </div>
                  </td>

                  {/* Designated Route */}
                  <td style={{ padding: "12px", color: textColor }}>
                    {req.route ? (
                      <span style={{ padding: "2px 6px", borderRadius: "4px", background: isDark ? "#2a2a2a" : "#eaeaea", fontSize: "12px" }}>
                        {req.route}
                      </span>
                    ) : (
                      <span style={{ fontStyle: "italic", color: subTextColor, fontSize: "12px" }}>—</span>
                    )}
                  </td>

                  {/* Pallets & Weight */}
                  <td style={{ padding: "12px", color: textColor }}>
                    <div style={{ display: "flex", gap: "8px", fontSize: "13px" }}>
                      {req.pallets !== null && (
                        <span><strong>{req.pallets}</strong> plts</span>
                      )}
                      {req.pallets !== null && req.weight !== null && <span style={{ opacity: 0.4 }}>|</span>}
                      {req.weight !== null && (
                        <span><strong>{req.weight}</strong> kg</span>
                      )}
                    </div>
                  </td>

                  {/* Booking Time */}
                  <td style={{ padding: "12px", color: textColor, fontWeight: "500" }}>
                    {req.booked_time ? (
                      <span style={{ color: isDark ? "#38bdf8" : "#0284c7" }}>
                        ⏰ {req.booked_time.substring(0, 5)}
                      </span>
                    ) : (
                      <span style={{ color: subTextColor }}>—</span>
                    )}
                  </td>

                  {/* Request comments */}
                  <td style={{ padding: "12px", color: textColor, maxWidth: "200px" }}>
                    {req.comments ? (
                      <div 
                        title={req.comments} 
                        style={{ 
                          whiteSpace: "nowrap", 
                          overflow: "hidden", 
                          textOverflow: "ellipsis", 
                          fontSize: "12px" 
                        }}
                      >
                        {req.comments}
                      </div>
                    ) : (
                      <span style={{ color: subTextColor, fontSize: "12px" }}>—</span>
                    )}
                  </td>

                  {/* Status Badge */}
                  <td style={{ padding: "12px" }}>
                    <span 
                      style={{ 
                        display: "inline-block",
                        padding: "4px 10px", 
                        borderRadius: "12px", 
                        fontSize: "11px", 
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        ...badgeStyle
                      }}
                    >
                      {req.status}
                    </span>
                    {req.status === "declined" && req.decline_reason && (
                      <div 
                        style={{ 
                          fontSize: "11px", 
                          color: isDark ? "#fca5a5" : "#c53030", 
                          marginTop: "4px", 
                          maxWidth: "150px" 
                        }}
                        title={req.decline_reason}
                      >
                        Reason: {req.decline_reason}
                      </div>
                    )}
                    {req.status === "accepted" && req.route_ref && (
                      <div 
                        style={{ 
                          fontSize: "11px", 
                          color: isDark ? "#38bdf8" : "#0284c7", 
                          marginTop: "4px", 
                          fontWeight: "bold"
                        }}
                      >
                        Ref: {req.route_ref}
                      </div>
                    )}
                    {(req.status === "accepted" || req.status === "declined") && (
                      <div style={{ fontSize: "9px", color: subTextColor, marginTop: "2px" }}>
                        By {processorName}
                      </div>
                    )}
                  </td>

                  {/* Actions Column */}
                  <td style={{ padding: "12px", textAlign: "right", verticalAlign: "middle" }}>
                    {req.status === "pending" ? (
                      canEdit ? (
                        /* Admin accept/decline choices for pending */
                        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", alignItems: "center" }}>
                          {declineReasonId === req.id ? (
                            <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                              <input
                                type="text"
                                value={declineReason}
                                onChange={(e) => setDeclineReason(e.target.value)}
                                placeholder="Enter reason..."
                                style={{ 
                                  padding: "6px 8px", 
                                  borderRadius: "4px", 
                                  border: isDark ? "1px solid #555" : "1px solid #ccc",
                                  background: isDark ? "#2d2d2d" : "#fff",
                                  color: textColor,
                                  fontSize: "12px",
                                  width: "120px"
                                }}
                                disabled={isProcessing}
                              />
                              <button
                                onClick={() => handleDeclineSubmit(req.id)}
                                disabled={isProcessing || declineReason.trim() === ""}
                                style={{ 
                                  padding: "4px 8px", 
                                  background: "#dc3545", 
                                  color: "#fff", 
                                  border: "none", 
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  fontSize: "12px"
                                }}
                              >
                                Go
                              </button>
                              <button
                                onClick={() => { setDeclineReasonId(null); setDeclineReason(""); }}
                                disabled={isProcessing}
                                style={{ 
                                  padding: "4px 8px", 
                                  background: "#6c757d", 
                                  color: "#fff", 
                                  border: "none", 
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  fontSize: "12px"
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <>
                              <button
                                onClick={() => onAccept(req.id)}
                                title="Accept Request"
                                style={{
                                  background: "#28a745",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "4px",
                                  width: "32px",
                                  height: "32px",
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "transform 0.1s ease"
                                }}
                                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                              >
                                <FontAwesomeIcon icon={faCheck} style={{ width: 15 }} />
                              </button>
                              <button
                                onClick={() => setDeclineReasonId(req.id)}
                                title="Decline Request"
                                style={{
                                  background: "#dc3545",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "4px",
                                  width: "32px",
                                  height: "32px",
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "transform 0.1s ease"
                                }}
                                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                              >
                                <FontAwesomeIcon icon={faTimes} style={{ width: 15 }} />
                              </button>
                            </>
                          )}
                        </div>
                      ) : (
                        /* View-only user self-cancellation option */
                        String(req.profile_id) === String(currentUserId) ? (
                          <button
                            onClick={() => onCancel(req.id)}
                            title="Cancel Request"
                            style={{
                              background: "none",
                              color: isDark ? "#ef4444" : "#dc3545",
                              border: isDark ? "1px solid rgba(239, 68, 68, 0.4)" : "1px solid rgba(220, 53, 69, 0.4)",
                              borderRadius: "4px",
                              padding: "6px 12px",
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              fontSize: "12px",
                              transition: "all 0.1s ease"
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.background = isDark ? "rgba(239,68,68,0.1)" : "rgba(220,53,69,0.05)";
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.background = "none";
                            }}
                          >
                            <FontAwesomeIcon icon={faTrashAlt} style={{ width: 15 }} /> Cancel
                          </button>
                        ) : (
                          <span style={{ color: subTextColor, fontSize: "13px" }}>—</span>
                        )
                      )
                    ) : (req.status === "accepted" || req.status === "declined") && canEdit ? (
                      /* Admin edit processed request options */
                      <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", alignItems: "center" }}>
                        {activeEditingId !== req.id ? (
                          <button
                            onClick={() => setActiveEditingId(req.id)}
                            title="Edit Processed Request"
                            style={{
                              background: "none",
                              color: isDark ? "#38bdf8" : "#0284c7",
                              border: isDark ? "1px solid rgba(56, 189, 248, 0.4)" : "1px solid rgba(2, 132, 199, 0.4)",
                              borderRadius: "4px",
                              padding: "4px 8px",
                              cursor: "pointer",
                              fontSize: "12px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              transition: "all 0.1s ease"
                            }}
                            onMouseOver={(e) => e.currentTarget.style.background = isDark ? "rgba(56,189,248,0.1)" : "rgba(2,132,199,0.05)"}
                            onMouseOut={(e) => e.currentTarget.style.background = "none"}
                          >
                            <FontAwesomeIcon icon={faPen} style={{ width: 11 }} /> Edit
                          </button>
                        ) : (
                          /* Opened Edit Mode choices */
                          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                            {declineReasonId === req.id ? (
                              <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                                <input
                                  type="text"
                                  value={declineReason}
                                  onChange={(e) => setDeclineReason(e.target.value)}
                                  placeholder="Decline reason..."
                                  style={{ 
                                    padding: "6px 8px", 
                                    borderRadius: "4px", 
                                    border: isDark ? "1px solid #555" : "1px solid #ccc",
                                    background: isDark ? "#2d2d2d" : "#fff",
                                    color: textColor,
                                    fontSize: "12px",
                                    width: "120px"
                                  }}
                                  disabled={isProcessing}
                                />
                                <button
                                  onClick={async () => {
                                    await handleDeclineSubmit(req.id);
                                    setActiveEditingId(null);
                                  }}
                                  disabled={isProcessing || declineReason.trim() === ""}
                                  style={{ 
                                    padding: "4px 8px", 
                                    background: "#dc3545", 
                                    color: "#fff", 
                                    border: "none", 
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "12px"
                                  }}
                                >
                                  Go
                                </button>
                                <button
                                  onClick={() => { setDeclineReasonId(null); setDeclineReason(""); }}
                                  disabled={isProcessing}
                                  style={{ 
                                    padding: "4px 8px", 
                                    background: "#6c757d", 
                                    color: "#fff", 
                                    border: "none", 
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "12px"
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <>
                                {req.status === "declined" ? (
                                  /* Accept previously declined request */
                                  <button
                                    onClick={async () => {
                                      await onAccept(req.id);
                                      setActiveEditingId(null);
                                    }}
                                    title="Approve / Accept Request"
                                    style={{
                                      background: "#28a745",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "4px",
                                      padding: "4px 10px",
                                      fontSize: "12px",
                                      cursor: "pointer",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "4px"
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faCheck} style={{ width: 11 }} /> Accept
                                  </button>
                                ) : (
                                  /* Decline previously accepted request */
                                  <button
                                    onClick={() => setDeclineReasonId(req.id)}
                                    title="Decline / Revoke Request"
                                    style={{
                                      background: "#dc3545",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "4px",
                                      padding: "4px 10px",
                                      fontSize: "12px",
                                      cursor: "pointer",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "4px"
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faTimes} style={{ width: 11 }} /> Decline
                                  </button>
                                )}
                                <button
                                  onClick={() => setActiveEditingId(null)}
                                  title="Cancel Edit"
                                  style={{
                                    background: "#6c757d",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "4px",
                                    padding: "4px 10px",
                                    fontSize: "12px",
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px"
                                  }}
                                >
                                  <FontAwesomeIcon icon={faUndo} style={{ width: 11 }} /> Cancel
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: subTextColor, fontSize: "13px" }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
};

export default JobRequestsTable;
